import React, { useRef, useEffect, useState, useCallback } from 'react';
import styles from './Latest.module.scss';
import Text from '../../../atoms/text/Text';
import NewsTab from './components/news-tab/NewsTab';
import NewsletterTab from './components/newsletter-tab/NewsletterTab';
import EventsTab from './components/events-tab/EventsTab';
import MediaTab from './components/media-tab/MediaTab';
import useIsRtl from '../../../../app/helpers/isRtl';
import CalendarDropdown from './components/calendar-tab/calendar-dropdown/CalendarDropdown';
import Calendar from './components/calendar-tab/Calendar';

const CALENDAR_SUBTABS = [
  { title: 'home.general_calendar', id: '0', type: 'general' },
  { title: 'home.higher_education_calendar', id: '1', type: 'higherEducation' },
  { title: 'home.evening_studies_calendar', id: '2', type: 'eveningStudies' },
  { title: 'home.cadets_calendar', id: '3', type: 'cadetsCalendar' },
];

const CalendarWrapper = () => {
  const isRtl = useIsRtl();
  const [subTab, setSubTab] = useState(0);
  const [activeYearId, setActiveYearId] = useState(
    '62B02A29-E9D2-463D-93EB-A75622983565'
  );
  const selected = CALENDAR_SUBTABS[subTab];

  return (
    <div className={styles['calendar-panel']}>
      <Calendar
        id={selected.id}
        activeTab={subTab}
        activeYearId={activeYearId}
        setActiveYearId={setActiveYearId}
        tabs={
          <div
            className={styles['dropdown-container']}
            dir={isRtl ? 'rtl' : ''}
          >
            <CalendarDropdown
              CALENDAR_SUBTABS={CALENDAR_SUBTABS}
              subTab={subTab}
              setSubTab={setSubTab}
            />
          </div>
        }
      />
    </div>
  );
};

const TABS = [
  { title: 'home.news', key: 'news', Component: NewsTab },
  { title: 'home.events', key: 'events', Component: EventsTab },
  {
    title: 'NewsLetter.NEWSLETTER',
    key: 'newsletter',
    Component: NewsletterTab,
  },
  { title: 'MediaGallery.MEDIA GALLERY', key: 'media', Component: MediaTab },
  {
    title: 'home.academic_calendar',
    key: 'calendar',
    Component: CalendarWrapper,
  },
];

const Latest = () => {
  const isRtl = useIsRtl();
  const [activeTab, setActiveTab] = useState('news');
  const [isLoading, setIsLoading] = useState(false);

  const tabsRef = useRef(null);
  const tabRefs = useRef({});

  const centerTab = useCallback((key) => {
    const viewport = tabsRef.current;
    const el = tabRefs.current[key];
    if (!viewport || !el) return;

    el.focus({ preventScroll: true });

    const vpRect = viewport.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const current = viewport.scrollLeft;
    const elCenter =
      elRect.left - vpRect.left + viewport.scrollLeft + elRect.width / 2;
    const target = elCenter - vpRect.width / 2;

    viewport.scrollTo({ left: target, behavior: 'smooth' });
  }, []);

  const handleTabChange = (tabKey) => {
    setIsLoading(true);
    setActiveTab(tabKey);
    requestAnimationFrame(() => centerTab(tabKey));
    setTimeout(() => setIsLoading(false), 1);
  };

  useEffect(() => {
    centerTab(activeTab);
  }, [activeTab, isRtl, centerTab]);

  return (
    <div className={styles['Latest-container']}>
      <Text
        title="home.academy_latest"
        variant="text-bold-italic-40"
        className={`${styles.title} ${activeTab === 'calendar' ? styles['cal-title'] : ''}`}
      />

      <div className={styles.wrapper}>
        <div
          className={`${styles.tabs} ${isRtl ? 'rtl' : ''}`}
          ref={tabsRef}
          role="tablist"
          aria-label="Latest sections"
        >
          {TABS.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                ref={(el) => (tabRefs.current[tab.key] = el)}
                className={`${styles.tab} ${isActive ? styles.active : ''}`}
                onClick={() => handleTabChange(tab.key)}
                role="tab"
                aria-selected={isActive}
                aria-controls={`panel-${tab.key}`}
                tabIndex={isActive ? 0 : -1}
                type="button"
              >
                <Text
                  title={tab.title}
                  variant="text-regular-20"
                  className={styles['tab-text']}
                  color={isActive ? '#008755' : '#757574'}
                />
              </button>
            );
          })}
        </div>

        <div className={styles['tab-content']}>
          {TABS.map(({ key, Component }) => {
            const isActive = activeTab === key;
            return (
              <section
                key={key}
                id={`panel-${key}`}
                className={
                  isActive ? styles['panel-active'] : styles['panel-hidden']
                }
                aria-hidden={!isActive}
                role="tabpanel"
              >
                <div style={{ visibility: isLoading ? 'hidden' : 'visible' }}>
                  <Component />
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Latest;
